import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './BulkOrderDetail.css';

interface OrderItem {
  formula_id: number;
  formula_code: string;
  formula_name: string;
  animal_type: string;
  feed_grade: string;
  quantity_lbs: string;
  package_type: string;
  form: string;
  unit_price_per_lb: string;
  line_total: string;
}

interface BulkOrder {
  order_number: string;
  order_id: number;
  order_date: string;
  delivery_date: string;
  status: string;
  status_display: string;
  shipping_method: string;
  total_weight_lbs: string;
  subtotal: string;
  tax_amount: string;
  total_amount: string;
  paid_amount: string;
  notes: string;
  special_instructions: string;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#666',
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  IN_PRODUCTION: '#8b5cf6',
  MILLED: '#10b981',
  PACKAGING: '#06b6d4',
  READY_TO_SHIP: '#22c55e',
  SHIPPED: '#0ea5e9',
  DELIVERED: '#14b8a6',
  CANCELLED: '#ef4444',
};

const BulkOrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();

  const [order, setOrder] = useState<BulkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    if (orderNumber) {
      loadOrder();
    }
  }, [hasLocalAccess, orderNumber, navigate]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bridgeClubAPI.getBulkOrderDetail(orderNumber!);
      setOrder(data);
    } catch (err: any) {
      console.error('Failed to load order:', err);
      setError(err.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (!hasLocalAccess) {
    return null;
  }

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="bulk-order-detail">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error || !order) {
    return (
      <BridgeClubLayout>
        <div className="bulk-order-detail">
          <div className="error-state">
            <p>{error || 'Order not found'}</p>
            <Link to="/bridge-club/shop" className="back-link">
              Back to Shop
            </Link>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="bulk-order-detail">
        <div className="page-header">
          <Link to="/bridge-club/shop" className="back-link">
            Back to Shop
          </Link>
          <h1>Order {order.order_number}</h1>
        </div>

        {/* Status Banner */}
        <div
          className="status-banner"
          style={{ backgroundColor: STATUS_COLORS[order.status] || '#666' }}
        >
          <span className="status-label">Order Status:</span>
          <span className="status-value">{order.status_display}</span>
        </div>

        {/* Order Info */}
        <div className="order-info">
          <div className="info-row">
            <span className="label">Order Date:</span>
            <span className="value">{new Date(order.order_date).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <span className="label">Requested Pickup:</span>
            <span className="value">{new Date(order.delivery_date).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <span className="label">Total Weight:</span>
            <span className="value">{parseFloat(order.total_weight_lbs).toLocaleString()} lbs</span>
          </div>
        </div>

        {/* Order Items */}
        <div className="order-section">
          <h2>Order Items</h2>
          <div className="items-list">
            {order.items.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-main">
                  <span className="item-name">{item.formula_name}</span>
                  <span className="item-details">
                    {item.quantity_lbs} lbs - {item.form}
                  </span>
                </div>
                <div className="item-price">
                  <span className="price-per-lb">
                    ${parseFloat(item.unit_price_per_lb).toFixed(4)}/lb
                  </span>
                  <span className="line-total">${parseFloat(item.line_total).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Totals */}
        <div className="order-totals">
          <div className="total-row">
            <span>Subtotal</span>
            <span>${parseFloat(order.subtotal).toFixed(2)}</span>
          </div>
          <div className="total-row">
            <span>Tax</span>
            <span>${parseFloat(order.tax_amount).toFixed(2)}</span>
          </div>
          <div className="total-row total">
            <span>Total</span>
            <span>${parseFloat(order.total_amount).toFixed(2)}</span>
          </div>
          {parseFloat(order.paid_amount) > 0 && (
            <div className="total-row paid">
              <span>Paid</span>
              <span>${parseFloat(order.paid_amount).toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Notes */}
        {order.special_instructions && (
          <div className="order-section">
            <h2>Payment Info</h2>
            <p className="notes-text">{order.special_instructions}</p>
          </div>
        )}

        {/* Order Status Timeline */}
        <div className="order-section">
          <h2>Order Progress</h2>
          <div className="status-timeline">
            {['PENDING', 'CONFIRMED', 'IN_PRODUCTION', 'MILLED', 'READY_TO_SHIP'].map(
              (status, index) => {
                const statusOrder = ['PENDING', 'CONFIRMED', 'IN_PRODUCTION', 'MILLED', 'READY_TO_SHIP'];
                const currentIndex = statusOrder.indexOf(order.status);
                const isComplete = index <= currentIndex;
                const isCurrent = status === order.status;

                return (
                  <div
                    key={status}
                    className={`timeline-step ${isComplete ? 'complete' : ''} ${isCurrent ? 'current' : ''}`}
                  >
                    <div className="step-dot"></div>
                    <span className="step-label">
                      {status === 'PENDING' && 'Order Received'}
                      {status === 'CONFIRMED' && 'Confirmed'}
                      {status === 'IN_PRODUCTION' && 'Milling'}
                      {status === 'MILLED' && 'Packaging'}
                      {status === 'READY_TO_SHIP' && 'Ready for Pickup'}
                    </span>
                  </div>
                );
              }
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="order-actions">
          <Link to="/bridge-club/bulk-order" className="action-btn secondary">
            New Bulk Order
          </Link>
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default BulkOrderDetail;
